import React, { useState } from 'react';
import { fetchProxiedIcs } from '../lib/api';
import { doc, updateDoc, addDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { X, Calendar } from 'lucide-react';
import { motion } from 'motion/react';

// Inline ICS utilities — same logic as CalendarImporter.tsx
const parseICS = (text: string, appTeamName: string) => {
  const games: any[] = [];
  const vevents = text.split('BEGIN:VEVENT');
  vevents.shift();
  const rawEvents: { summary: string; dtstart: string }[] = [];
  vevents.forEach(event => {
    const summary = event.match(/SUMMARY:(.*)/)?.[1] || 'Unknown Game';
    if (summary.toLowerCase().includes('practice')) return;
    const dtstart = event.match(/DTSTART[:;](?:VALUE=DATE:)?(\d{8}T\d{6}Z?|\d{8})/)?.[1];
    if (dtstart) rawEvents.push({ summary, dtstart });
  });
  const teamTally: Record<string, number> = {};
  const extractTeams = (s: string) => {
    const m = s.match(/(.*?)(?:\s+at\s+|\s+@\s+|\s+vs\.?\s+|\s+v\.?\s+)(.*)/i);
    return m ? [m[1].trim(), m[2].trim()] : null;
  };
  rawEvents.forEach(e => {
    const t = extractTeams(e.summary);
    if (t) { teamTally[t[0]] = (teamTally[t[0]] || 0) + 1; teamTally[t[1]] = (teamTally[t[1]] || 0) + 1; }
  });
  let userIcsTeamName = '';
  const appLower = appTeamName.toLowerCase();
  for (const t of Object.keys(teamTally)) {
    if (t.toLowerCase().includes(appLower) || appLower.includes(t.toLowerCase())) { userIcsTeamName = t; break; }
  }
  if (!userIcsTeamName) {
    let max = 0;
    for (const [t, c] of Object.entries(teamTally)) { if (c > max) { max = c; userIcsTeamName = t; } }
  }
  rawEvents.forEach(e => {
    let opponent = e.summary; let isHome = false;
    const mAt = e.summary.match(/(.*?)(?:\s+at\s+|\s+@\s+)(.*)/i);
    const mVs = e.summary.match(/(.*?)(?:\s+vs\.?\s+|\s+v\.?\s+)(.*)/i);
    if (mAt) {
      if (mAt[2].trim() === userIcsTeamName) { isHome = true; opponent = mAt[1].trim(); }
      else { opponent = mAt[2].trim(); }
    } else if (mVs) {
      if (mVs[1].trim() === userIcsTeamName) { isHome = true; opponent = mVs[2].trim(); }
      else { opponent = mVs[2].trim(); }
    } else {
      opponent = e.summary.replace(/^Game vs\s+/i, '').replace(/^vs\.?\s+/i, '').trim();
    }
    const yr = e.dtstart.substring(0, 4), mo = e.dtstart.substring(4, 6), dy = e.dtstart.substring(6, 8);
    const hr = e.dtstart.substring(9, 11) || '00', mn = e.dtstart.substring(11, 13) || '00';
    games.push({ opponent, isHome, date: new Date(`${yr}-${mo}-${dy}T${hr}:${mn}:00Z`).toISOString(), status: 'scheduled', createdAt: new Date().toISOString() });
  });
  return games;
};
const fuzzyOpponentMatch = (a: string, b: string) => {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
  const na = norm(a), nb = norm(b);
  if (na === nb || na.includes(nb) || nb.includes(na)) return true;
  const wa = na.split(/\s+/).filter(w => w.length >= 3);
  const wb = new Set(nb.split(/\s+/).filter(w => w.length >= 3));
  return wa.some(w => wb.has(w));
};
const isSameDay = (a: string, b: string) => a.substring(0, 10) === b.substring(0, 10);

interface Props {
  teamId: string;
  teamName: string;
  onClose: () => void;
  onSuccess: (gameCount: number) => void;
}

export const ChecklistCalendarSync: React.FC<Props> = ({ teamId, teamName, onClose, onSuccess }) => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSync = async () => {
    if (!url.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchProxiedIcs(url.trim());
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to sync calendar.');
      }
      const text = await response.text();
      const games = parseICS(text, teamName);
      if (games.length === 0) throw new Error('No games found in this calendar.');

      const existingSnap = await getDocs(collection(db, `teams/${teamId}/games`));
      const existing = existingSnap.docs.map(d => d.data() as { opponent: string; date: string });

      let added = 0;
      for (const game of games) {
        const dup = existing.some(eg => isSameDay(eg.date, game.date) && fuzzyOpponentMatch(eg.opponent, game.opponent));
        if (!dup) {
          await addDoc(collection(db, `teams/${teamId}/games`), game);
          added++;
        }
      }

      await updateDoc(doc(db, 'teams', teamId), {
        'settings.onboardingChecklist.calendarSynced': true,
      });

      onSuccess(added);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-surface backdrop-blur-xl flex items-end justify-center p-4"
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        className="bg-canvas w-full max-w-sm rounded-3xl p-6 border border-hairline space-y-5 relative"
      >
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-ink-2 hover:text-ink transition-colors">
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-canvas rounded-xl flex items-center justify-center shrink-0">
            <Calendar className="w-5 h-5 text-accent-ink" />
          </div>
          <div>
            <h3 className="text-base font-black italic uppercase">Sync Your Schedule</h3>
            <p className="text-[11px] text-ink-2">Pull in your full season from GameChanger.</p>
          </div>
        </div>

        {/* Step-by-step GC instructions */}
        <div className="bg-surface border border-hairline rounded-2xl p-4 space-y-2">
          <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-3">How to get your link</p>
          {[
            'Open GameChanger',
            'Tap Team → Team Settings',
            'Tap Schedule Sync',
            'Tap Sync Schedule to your Calendar',
            'Tap Copy Calendar Link',
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 bg-raised rounded-full flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-[9px] font-black text-ink-2">{i + 1}</span>
              </div>
              <span className="text-xs text-ink-2">{step}</span>
            </div>
          ))}
        </div>

        {/* Paste field */}
        <div className="space-y-2">
          <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Paste your calendar link</label>
          <input
            type="url"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="webcal://…"
            className="w-full bg-surface border border-hairline rounded-xl px-4 py-3 text-sm font-medium outline-none focus:border-ink-mute transition-colors placeholder:text-ink-2"
            autoFocus
          />
          {error && <p className="text-[11px] text-danger">{error}</p>}
        </div>

        <button
          onClick={handleSync}
          disabled={!url.trim() || loading}
          className="w-full py-4 bg-ink hover:bg-ink disabled:opacity-40 text-surface font-black rounded-2xl uppercase italic transition-colors"
        >
          {loading ? 'Syncing…' : 'Sync Schedule →'}
        </button>
      </motion.div>
    </motion.div>
  );
};
